/* eslint-disable react-hooks/exhaustive-deps */
import { Autocomplete, Grid, SelectChangeEvent, useTheme } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import React, { useState, useCallback, useEffect } from 'react';

import { getCityCoverage } from '../../../../../../api/theHive/noticeCoverage';
import { CityLocation } from '../../../../../../api/theHive/noticeCoverage/types';
import { CustomTextField } from '../../../../../../components/CustomInput';
import {
  menuItemDefaultStyle,
  StyledTextField,
  textFieldDefaultStyle,
} from '../../../../../../components/UI/Typography';
import {
  IconAddMS,
  IconExpandMoreMS,
  IconDeleteMS,
} from '../../../../../../constants/icons';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import {
  CustomAccordion,
  StyledIconButton,
  StyledSelectUf,
} from '../CoverageAreaSection/ModalAddingCoverage/styles';
import { CitiesAccordionProps, UF_OPTIONS } from './types';

export function CitiesAccordion({
  onDataChange,
  expanded = false,
  onExpandedChange,
}: CitiesAccordionProps): JSX.Element {
  const [coverageName, setCoverageName] = useState('');
  const [selectedUF, setSelectedUF] = useState('');
  const [selectedCity, setSelectedCity] = useState<CityLocation | null>(null);
  const [displacementValue, setDisplacementValue] = useState('');
  const [microregions, setMicroregions] = useState<
    Array<{
      id: number;
      value: CityLocation | null;
      index: number;
    }>
  >([{ id: 1, value: null, index: 0 }]);
  const [cities, setCities] = useState<CityLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getErrorMessage } = useErrorMessage();
  const theme = useTheme();

  const updateParentData = useCallback(() => {
    if (selectedCity) {
      onDataChange?.({
        name: coverageName,
        selectedCity: selectedCity.name,
        cityId: selectedCity.id,
        displacementValue,
        microregions: microregions
          .filter((region) => region.value)
          .map((region) => ({
            id: region.value?.id || 0,
            value: region.value?.name || '',
          })),
      });
    }
  }, [selectedCity, displacementValue, microregions]);

  const fetchCitiesForUF = useCallback(async (ufId: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getCityCoverage(ufId);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data)
        throw new Error('Não foi possível carregar as cidades.');

      setCities(response.data);
    } catch (error) {
      setError(getErrorMessage(error));
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    updateParentData();
  }, [updateParentData]);

  const handleUFChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      event.stopPropagation();
      const ufId = event.target.value as string;
      setSelectedUF(ufId);
      setSelectedCity(null);
      setMicroregions([{ id: 1, value: null, index: 0 }]);
      if (ufId) {
        fetchCitiesForUF(Number(ufId));
      }
    },
    [fetchCitiesForUF]
  );

  const formatCurrency = useCallback((value: string): string => {
    const numericValue = value.replace(/\D/g, '');
    const amount = Number(numericValue) / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  }, []);

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ): void => {
    const target = event.target as HTMLElement;
    if (
      !target.closest('input') &&
      !target.closest('button') &&
      !target.closest('.MuiAutocomplete-root') &&
      !target.closest('.MuiSelect-root')
    ) {
      onExpandedChange?.(isExpanded);
    }
  };

  const handleDisplacementChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      const rawValue = event.target.value.replace(/\D/g, '');
      const formattedValue = formatCurrency(rawValue);
      setDisplacementValue(formattedValue);
    },
    [formatCurrency]
  );

  const handleMicroregionChange = useCallback(
    (id: number, value: CityLocation | null) => {
      setMicroregions((prevMicroregions) =>
        prevMicroregions.map((region) =>
          region.id === id ? { ...region, value } : region
        )
      );
    },
    []
  );

  const addMicroregion = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setMicroregions((prevMicroregions) => [
      ...prevMicroregions,
      {
        id: Date.now(),
        value: null,
        index: prevMicroregions.length,
      },
    ]);
  }, []);

  const removeMicroregion = useCallback(
    (id: number, event: React.MouseEvent) => {
      event.stopPropagation();
      setMicroregions((prevMicroregions) =>
        prevMicroregions.filter((region) => region.id !== id)
      );
    },
    []
  );

  const handleCityChange = (
    event: React.SyntheticEvent,
    newValue: CityLocation | null
  ): void => {
    event.stopPropagation();
    setSelectedCity(newValue);
  };

  const isMicroregionSelectDisabled = !selectedUF || !selectedCity;

  const autocompleteStyles = {
    ...textFieldDefaultStyle,
    width: '100%',
    fontSize: '14px',
    textAlign: 'start' as const,
    backgroundColor: 'transparent',
    '& .MuiOutlinedInput-input': {
      fontSize: '14px',
      backgroundColor: 'transparent',
      '&::placeholder': {
        color: theme.palette.common.grey,
        opacity: 1,
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: theme.palette.common.grey,
    },
    '& .MuiAutocomplete-endAdornment': {
      right: '8px',
    },
    '& .MuiAutocomplete-popper .MuiPaper-root': {
      backgroundColor: '#ffffff',
    },
  };

  return (
    <CustomAccordion expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary
        expandIcon={IconExpandMoreMS}
        aria-controls="panel-content"
        id="panel-header"
      >
        <Grid container spacing={1}>
          <Grid item xs={5}>
            <CustomTextField
              id="name"
              label="nome"
              value={coverageName}
              setValue={setCoverageName}
              maxLength={70}
            />
          </Grid>
          <Grid item xs={2}>
            <StyledSelectUf
              value={selectedUF}
              onChange={handleUFChange}
              onClick={(e) => e.stopPropagation()}
              displayEmpty
            >
              <MenuItem value="" disabled>
                UF*
              </MenuItem>
              {UF_OPTIONS.map((uf) => (
                <MenuItem
                  key={uf.id}
                  value={uf.id.toString()}
                  sx={menuItemDefaultStyle}
                >
                  {uf.uf}
                </MenuItem>
              ))}
            </StyledSelectUf>
          </Grid>
          <Grid item xs={5}>
            <Autocomplete
              value={selectedCity}
              onChange={handleCityChange}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              options={cities}
              getOptionLabel={(option) => option.name}
              loading={loading}
              disabled={!selectedUF}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              disablePortal={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Novo Polo*"
                  error={!!error}
                  helperText={error}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              )}
              componentsProps={{
                popper: {
                  onClick: (e) => e.stopPropagation(),
                  style: { height: '300px' },
                },
                paper: {
                  onClick: (e) => e.stopPropagation(),
                },
              }}
              sx={autocompleteStyles}
              ListboxProps={{
                style: { backgroundColor: 'white' },
              }}
            />
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ width: '90%' }}>
        <Grid container spacing={1} mt={0.5}>
          <Grid item xs={0.5} />
          <Grid item xs={4.5}>
            <StyledTextField
              label="Valor do Deslocamento"
              variant="outlined"
              value={displacementValue}
              onChange={handleDisplacementChange}
              disabled={!selectedCity}
              InputLabelProps={{
                style: {
                  fontSize: '14px',
                },
              }}
              inputProps={{
                inputMode: 'numeric',
                style: {
                  fontSize: '14px',
                },
                maxLength: 16,
              }}
              sx={{
                width: '100%',
              }}
            />
          </Grid>
          <Grid item xs={1}>
            <StyledIconButton
              onClick={addMicroregion}
              disabled={isMicroregionSelectDisabled}
            >
              {IconAddMS}
            </StyledIconButton>
          </Grid>
          {microregions.map((region) => (
            <Grow in key={region.id} timeout={300}>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={0.5} />
                  <Grid item xs={4.5}>
                    <Autocomplete
                      componentsProps={{
                        popper: {
                          onClick: (e) => e.stopPropagation(),
                          style: { width: '220px', height: '300px' },
                        },
                        paper: {
                          onClick: (e) => e.stopPropagation(),
                        },
                      }}
                      disablePortal={false}
                      value={region.value}
                      onChange={(_, newValue) =>
                        handleMicroregionChange(region.id, newValue)
                      }
                      options={cities}
                      getOptionLabel={(option) => option.name}
                      loading={loading}
                      disabled={isMicroregionSelectDisabled}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Microregião"
                          error={!!error}
                          helperText={error}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      )}
                      sx={autocompleteStyles}
                      ListboxProps={{
                        style: { backgroundColor: 'white' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <StyledIconButton
                      onClick={(e) => removeMicroregion(region.id, e)}
                      disabled={isMicroregionSelectDisabled}
                    >
                      {IconDeleteMS}
                    </StyledIconButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grow>
          ))}
        </Grid>
      </AccordionDetails>
    </CustomAccordion>
  );
}
